import { B2BOpportunity } from '~/features/market/job-opportunities/types';
import { JobListing } from '~/types/hlx/jobListing';
import { MsaasJobListing } from '~/types/hlx/msaasJobListing';
import { MsaasNegotiation } from '~/types/hlx/msaasNegotiation';
import { getData } from '~/utils/service';

export type JobListingResponseSummary = {
  status: string;
};

export const fetchJobListings = (employerId: string): Promise<JobListing[]> =>
  getData('jobListing', { pathParams: { employerId } });

export const fetchJobListingResponses = (): Promise<JobListingResponseSummary[]> =>
  getData('providerJobResponses', { params: { status: 'all' } });

export const fetchB2BListings = (employerId: string): Promise<MsaasJobListing[]> =>
  getData('msaasListing', { pathParams: { employerId } });

const isNewerNegotiation = (candidate: MsaasNegotiation, current: MsaasNegotiation): boolean => {
  const candidateVersion = candidate.version ?? Number.NEGATIVE_INFINITY;
  const currentVersion = current.version ?? Number.NEGATIVE_INFINITY;

  if (candidateVersion !== currentVersion) {
    return candidateVersion > currentVersion;
  }

  return candidate.id > current.id;
};

export const getLatestNegotiationsByJobResponse = (
  negotiations: MsaasNegotiation[]
): MsaasNegotiation[] => {
  const latestByJobResponse = new Map<number, MsaasNegotiation>();

  negotiations.forEach((negotiation) => {
    const current = latestByJobResponse.get(negotiation.jobResponseId);
    if (!current || isNewerNegotiation(negotiation, current)) {
      latestByJobResponse.set(negotiation.jobResponseId, negotiation);
    }
  });

  return Array.from(latestByJobResponse.values());
};

export const fetchEmployerNegotiations = async (
  employerId: string
): Promise<MsaasNegotiation[]> => {
  const negotiations = await getData<MsaasNegotiation[]>('msaasNegotiationsByEmployer', {
    pathParams: { employerId },
  });
  return getLatestNegotiationsByJobResponse(negotiations);
};

export const fetchB2BOpportunities = (): Promise<B2BOpportunity[]> =>
  getData('b2bJobOpportunities', { params: { status: 'all' } });

export const fetchAcceptedB2BOpportunities = (): Promise<B2BOpportunity[]> =>
  getData('b2bJobOpportunities', { params: { status: 'accepted' } });
